/**
 * Mobile bridge — the single place the web application knows about native
 * (Android WebView) functionality.
 *
 * Android is detected only by the presence of a real injected bridge object.
 * The current Android app injects its JavaScript interface as `window.mobile`;
 * `window.AndroidBridge` is kept as a compatibility alias. The `appVersion` /
 * `bridgeVersion` query parameters are captured at startup and persisted, but
 * they are never used to decide whether native features are available — any
 * browser user can add them, so feature detection relies on the bridge itself.
 *
 * Wire contract: native `@JavascriptInterface` methods accept JSON strings and
 * return `Boolean`. Callers must convert domain objects into the native wire
 * payload before crossing the bridge, and treat an explicit `false` return
 * value as a native failure.
 */

export type ShareRequest = {
    title?: string;
    text?: string;
    url?: string;
};

export type CalendarEvent = {
    title: string;
    start: string;
    end: string;
    description?: string;
    location?: string;
    id?: string;
};

/**
 * Payload Android expects for `addCalendarEvent(String)`. Dates are epoch
 * milliseconds; do NOT send ISO `{ start, end }` fields to the bridge.
 */
export type NativeCalendarEventPayload = {
    title: string;
    startMillis: number;
    endMillis: number;
    description?: string;
    location?: string;
};

export type HapticType =
    | "light"
    | "medium"
    | "strong"
    | "success"
    | "warning"
    | "gameover";

export type ReminderRequest = {
    id: string;
    title: string;
    channel?: string;
    startTime: string;
    minutesBefore?: number;
};

export type BridgeCapability =
    | "share"
    | "calendar"
    | "haptic"
    | "openExternal"
    | "notifications"
    | "gameMode";

type NativeBridge = {
    appVersion?: () => string | number;
    bridgeVersion?: () => string | number;
    capabilities?: BridgeCapability[];
    share?: (payload: string) => boolean;
    addCalendarEvent?: (payload: string) => boolean;
    haptic?: (type: string) => boolean;
    scheduleReminder?: (payload: string) => boolean;
    openExternal?: (url: string) => boolean;
    enterGameMode?: () => boolean;
    exitGameMode?: () => boolean;
};

declare global {
    interface Window {
        /** Current Android bridge name. */
        mobile?: NativeBridge;
        /** Legacy alias, kept for backwards/forwards compatibility. */
        AndroidBridge?: NativeBridge;
    }
}

const APP_VERSION_KEY = "appVersion";
const BRIDGE_VERSION_KEY = "bridgeVersion";

// Bridge version -> capabilities, mirroring the native side.
const CAPABILITIES_BY_BRIDGE_VERSION: Record<number, BridgeCapability[]> = {
    1: ["share", "calendar", "haptic", "openExternal"],
    2: [
        "share",
        "calendar",
        "haptic",
        "openExternal",
        "notifications",
        "gameMode",
    ],
};

const METHOD_FOR_CAPABILITY: Record<BridgeCapability, keyof NativeBridge> = {
    share: "share",
    calendar: "addCalendarEvent",
    haptic: "haptic",
    openExternal: "openExternal",
    notifications: "scheduleReminder",
    gameMode: "enterGameMode",
};

function readStored(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStored(key: string, value: string) {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        // Ignore storage failures; the value is only a hint.
    }
}

/**
 * Capture `appVersion` and `bridgeVersion` from the initial page load and
 * persist them so later client-side navigation (which drops query
 * parameters) does not lose them. Call once at application startup.
 */
export function initMobileBridge() {
    if (typeof window === "undefined" || !window.location) return;
    const params = new URLSearchParams(window.location.search);
    const appVersionParam = params.get("appVersion");
    if (appVersionParam) {
        writeStored(APP_VERSION_KEY, appVersionParam);
    }
    const bridgeVersionParam = params.get("bridgeVersion");
    if (bridgeVersionParam) {
        writeStored(BRIDGE_VERSION_KEY, bridgeVersionParam);
    }

    if (process.env.NODE_ENV !== "production") {
        console.info("[mobileBridge]", {
            detected: isApp(),
            appVersion: appVersion(),
            bridgeVersion: bridgeVersion(),
            bridgeName: window.mobile
                ? "mobile"
                : window.AndroidBridge
                    ? "AndroidBridge"
                    : null,
        });
    }
}

function nativeBridge(): NativeBridge | null {
    if (typeof window === "undefined") return null;
    return window.mobile ?? window.AndroidBridge ?? null;
}

/** True only when a real native bridge object is present in the WebView. */
export function isApp(): boolean {
    return nativeBridge() !== null;
}

/** Android application (APK) version captured from the initial load. */
export function appVersion(): string | null {
    const bridge = nativeBridge();
    if (bridge?.appVersion) {
        const value = bridge.appVersion();
        if (value !== undefined && value !== null) return String(value);
    }
    return readStored(APP_VERSION_KEY);
}

/** JavaScript <-> native API contract version. */
export function bridgeVersion(): number | null {
    const bridge = nativeBridge();
    if (bridge?.bridgeVersion) {
        const value = bridge.bridgeVersion();
        if (value !== undefined && value !== null) return Number(value);
    }
    const stored = readStored(BRIDGE_VERSION_KEY);
    if (stored) return Number(stored);
    return null;
}

/**
 * Capability detection. Only the real bridge can grant capabilities; the
 * query-parameter versions are never trusted for this.
 */
export function supports(capability: BridgeCapability): boolean {
    const bridge = nativeBridge();
    if (!bridge) return false;

    if (Array.isArray(bridge.capabilities)) {
        return bridge.capabilities.includes(capability);
    }

    if (bridge.bridgeVersion) {
        const version = Number(bridge.bridgeVersion());
        if (Number.isFinite(version)) {
            return (CAPABILITIES_BY_BRIDGE_VERSION[version] || []).includes(
                capability
            );
        }
    }

    return typeof bridge[METHOD_FOR_CAPABILITY[capability]] === "function";
}

function callBridge(
    capability: BridgeCapability,
    methodName: keyof NativeBridge,
    ...args: unknown[]
): boolean {
    const bridge = nativeBridge();
    if (!bridge || !supports(capability)) return false;
    const method = bridge[methodName];
    if (typeof method !== "function") return false;
    try {
        const result = (method as unknown as (...params: unknown[]) => unknown)(
            ...args
        );
        // Android @JavascriptInterface methods currently return Boolean.
        return result !== false;
    } catch (error) {
        if (process.env.NODE_ENV !== "production") {
            console.warn(`[mobileBridge] ${String(methodName)} failed`, error);
        }
        // A broken native call should never break the web app.
        return false;
    }
}

async function shareWebFallback(request: ShareRequest): Promise<boolean> {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        try {
            await navigator.share({
                title: request.title,
                text: request.text,
                url: request.url,
            });
            return true;
        } catch {
            // Cancelled or unsupported — fall through to copy.
        }
    }
    const url = request.url || window.location.href;
    try {
        await navigator.clipboard.writeText(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * Share via the Android Sharesheet when available, otherwise fall back to
 * the Web Share API and finally to copying the URL to the clipboard.
 */
export async function share(request: ShareRequest): Promise<boolean> {
    const nativePayload = JSON.stringify(request);
    if (callBridge("share", "share", nativePayload)) return true;
    return shareWebFallback(request);
}

function formatUtcTimestamp(input: string): string | null {
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return null;
    const pad = (value: number) => String(value).padStart(2, "0");
    return (
        `${date.getUTCFullYear()}` +
        `${pad(date.getUTCMonth() + 1)}` +
        `${pad(date.getUTCDate())}` +
        `T${pad(date.getUTCHours())}` +
        `${pad(date.getUTCMinutes())}` +
        `${pad(date.getUTCSeconds())}Z`
    );
}

function formatIcsUtc(input: string): string | null {
    return formatUtcTimestamp(input);
}

/**
 * Open a pre-filled event in Google Calendar. Browsers do not expose a
 * standard API for inserting into the user's default calendar, so a calendar
 * web app is the only path that behaves like "Add to calendar" without first
 * downloading a file. This runs synchronously while the button gesture is
 * active so the new tab is not treated as an asynchronous popup.
 */
function openGoogleCalendar(event: CalendarEvent): boolean {
    const startMillis = Date.parse(event.start);
    const endMillis = Date.parse(event.end);
    const start = formatIcsUtc(event.start);
    const end = formatIcsUtc(event.end);
    if (
        !start ||
        !end ||
        !event.title.trim() ||
        !Number.isFinite(startMillis) ||
        !Number.isFinite(endMillis) ||
        endMillis <= startMillis
    ) {
        return false;
    }

    const params = new URLSearchParams({
        action: "TEMPLATE",
        text: event.title,
        dates: `${start}/${end}`,
    });
    if (event.description) params.set("details", event.description);
    if (event.location) params.set("location", event.location);
    const calendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;

    // A WebView generally cannot create target=_blank windows. If the native
    // calendar insert failed, use its external-link capability so the browser
    // can still open the pre-filled event.
    if (callBridge("openExternal", "openExternal", calendarUrl)) return true;

    try {
        const anchor = document.createElement("a");
        anchor.href = calendarUrl;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        return true;
    } catch {
        return false;
    }
}

function addCalendarWebFallback(event: CalendarEvent): boolean {
    if (!toNativeCalendarPayload(event)) return false;
    return openGoogleCalendar(event);
}

/**
 * Convert the friendly domain event into the native wire payload.
 * Returns null when the dates are invalid or the end precedes the start.
 */
export function toNativeCalendarPayload(
    event: CalendarEvent
): NativeCalendarEventPayload | null {
    const startMillis = Date.parse(event.start);
    const endMillis = Date.parse(event.end);

    if (
        !Number.isFinite(startMillis) ||
        !Number.isFinite(endMillis) ||
        endMillis <= startMillis
    ) {
        return null;
    }

    return {
        title: event.title,
        startMillis,
        endMillis,
        description: event.description,
        location: event.location,
    };
}

/**
 * Add an event to the calendar. Inside the app this opens the Android
 * calendar event creation screen (the user stays in control and confirms);
 * in a browser it opens a pre-filled Google Calendar event in a new tab. A
 * failure is reported to the caller; no file is downloaded implicitly.
 */
export async function addCalendarEvent(event: CalendarEvent): Promise<boolean> {
    const nativePayload = toNativeCalendarPayload(event);

    if (
        nativePayload &&
        callBridge(
            "calendar",
            "addCalendarEvent",
            JSON.stringify(nativePayload)
        )
    ) {
        return true;
    }

    return addCalendarWebFallback(event);
}

/**
 * Subtle haptic feedback. Does nothing in browsers and in apps without
 * haptic support. `"gameover"` is mapped to `"strong"` until the Android
 * bridge gains a GAMEOVER haptic name.
 */
export function haptic(type: HapticType): void {
    const nativeType = type === "gameover" ? "strong" : type;
    callBridge("haptic", "haptic", nativeType);
}

/**
 * Open a URL outside the WebView (usually via the Android side). In a
 * browser this simply opens a new tab.
 */
export function openExternal(url: string): boolean {
    if (callBridge("openExternal", "openExternal", url)) return true;
    try {
        window.open(url, "_blank", "noreferrer");
        return true;
    } catch {
        return false;
    }
}

/** Schedule a native notification (only meaningful inside the app). */
export function scheduleReminder(reminder: ReminderRequest): boolean {
    return callBridge(
        "notifications",
        "scheduleReminder",
        JSON.stringify(reminder)
    );
}

/** Request an immersive native game mode (no-op in browsers). */
export function enterGameMode(): boolean {
    return callBridge("gameMode", "enterGameMode");
}

/** Leave the native game mode and restore normal configuration. */
export function exitGameMode(): boolean {
    return callBridge("gameMode", "exitGameMode");
}

type BackListener = () => void;

/**
 * Register a listener for the Android Back button/gesture. Returns an
 * unsubscribe function. In browsers this is a no-op.
 *
 * The Android side initiates the protocol by evaluating:
 * `window.dispatchEvent(new Event("nativeBack"))`.
 */
export function onNativeBack(listener: BackListener): () => void {
    if (typeof window === "undefined") return () => {};
    const handler = () => listener();
    window.addEventListener("nativeBack", handler);
    return () => window.removeEventListener("nativeBack", handler);
}

export const mobile = {
    isApp,
    appVersion,
    bridgeVersion,
    supports,
    share,
    addCalendarEvent,
    haptic,
    openExternal,
    scheduleReminder,
    enterGameMode,
    exitGameMode,
    onNativeBack,
};
